// Comptes usagers (D-015, H7-01, H7-02, brief L1-A) : inscription par téléphone ou courriel avec
// code à usage unique, connexion, réinitialisation du mot de passe, profil et identité déclarée,
// vérification de l'identité au guichet, représentations, coffre de pièces.
import { z } from 'zod';
import { schemaNouveauMotDePasse } from './auth';

const dateCivile = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Date au format AAAA-MM-JJ');
const codePiece = z.string().regex(/^[a-z][a-z0-9_]{1,62}$/, 'Code de type de pièce invalide');
export const schemaCodeCollectivite = z.string().trim().toUpperCase().regex(/^[A-Z0-9]{2,8}$/, 'Code de collectivité invalide');

/**
 * Téléphone normalisé : espaces, points et tirets retirés, 00 initial remplacé par +, indicatif du
 * Gabon (+241) ajouté à un numéro national (hypothèse : usagers majoritairement gabonais).
 */
export function normaliserTelephone(brut: string): string {
  let t = brut.trim().replace(/[\s.-]/g, '');
  if (t.startsWith('00')) t = `+${t.slice(2)}`;
  if (!t.startsWith('+')) t = `+241${t}`;
  return t;
}

/** Identifiant de connexion d'un usager : courriel en minuscules, ou téléphone normalisé. */
export function normaliserIdentifiantUsager(brut: string): string {
  const v = brut.trim();
  return v.includes('@') ? v.toLowerCase() : normaliserTelephone(v);
}

export const schemaTelephone = z.string().trim().regex(/^\+?[0-9][0-9 .-]{5,19}$/, 'Numéro de téléphone invalide');
export const schemaIdentifiantUsager = z.string().trim().min(3).max(254).transform(normaliserIdentifiantUsager);
export const schemaCodeUnique = z.string().trim().regex(/^\d{6}$/, 'Code à six chiffres attendu');
export const CANAUX_CODE = ['sms', 'courriel'] as const;

export const schemaInscription = z
  .strictObject({
    telephone: schemaTelephone.optional(),
    courriel: z.string().trim().toLowerCase().email('Courriel invalide').max(254).optional(),
    canal: z.enum(CANAUX_CODE),
    mot_de_passe: schemaNouveauMotDePasse,
    nom: z.string().trim().min(1).max(120),
    prenoms: z.string().trim().max(200).optional(),
  })
  .refine((v) => v.telephone || v.courriel, { message: 'Téléphone ou courriel obligatoire', path: ['telephone'] })
  .refine((v) => v.canal !== 'sms' || v.telephone, { message: 'Le code par SMS exige un téléphone', path: ['canal'] })
  .refine((v) => v.canal !== 'courriel' || v.courriel, { message: 'Le code par courriel exige un courriel', path: ['canal'] });

export const schemaConfirmationInscription = z.strictObject({ identifiant: schemaIdentifiantUsager, code: schemaCodeUnique });
export const schemaConnexionUsager = z.strictObject({ identifiant: schemaIdentifiantUsager, mot_de_passe: z.string().min(1).max(256) });
export const schemaDemandeReinitialisation = z.strictObject({ identifiant: schemaIdentifiantUsager });
export const schemaReinitialisation = z.strictObject({
  identifiant: schemaIdentifiantUsager,
  code: schemaCodeUnique,
  nouveau_mot_de_passe: schemaNouveauMotDePasse,
});

export const schemaIdentiteDeclaree = z.strictObject({
  date_naissance: dateCivile.optional(),
  lieu_naissance: z.string().trim().max(200).optional(),
  adresse: z.string().trim().max(300).optional(),
  quartier: z.string().trim().max(120).optional(),
  ville: z.string().trim().max(120).optional(),
});

export const schemaModificationProfil = z.strictObject({
  nom: z.string().trim().min(1).max(120).optional(),
  prenoms: z.string().trim().max(200).optional(),
  courriel: z.string().trim().toLowerCase().email('Courriel invalide').max(254).nullable().optional(),
  canal_prefere: z.enum(CANAUX_CODE).nullable().optional(),
  identite_declaree: schemaIdentiteDeclaree.optional(),
});

export const TYPES_PIECE_IDENTITE = ['carte_nationale_identite', 'passeport', 'titre_sejour', 'permis_conduire', 'acte_naissance', 'autre'] as const;

/** Vérification au guichet : la pièce est vue, son type et sa date sont notés ; son numéro n'est pas conservé. */
export const schemaVerificationIdentite = z.strictObject({
  type_piece: z.enum(TYPES_PIECE_IDENTITE),
  date_piece: dateCivile,
  observations: z.string().trim().max(500).optional(),
});

export const TYPES_ENTITE = ['entreprise', 'association', 'personne'] as const;

/** Champs texte du formulaire multipart de POST /usager/representations (fichiers « mandat » et « extrait »). */
export const schemaDemandeRepresentation = z.strictObject({
  collectivite: schemaCodeCollectivite,
  entite_nom: z.string().trim().min(2).max(300),
  entite_type: z.enum(TYPES_ENTITE),
  entite_registre: z.string().trim().max(100).optional(),
});

export const schemaValidationRepresentation = z.strictObject({ fin: dateCivile });
export const schemaRefusRepresentation = z.strictObject({ motif: z.string().trim().min(3).max(1000) });
export const schemaFiltreRepresentations = z.object({ statut: z.enum(['en_attente', 'validee', 'refusee']).optional() });

/** Champs texte du formulaire multipart de POST /usager/pieces (fichier « fichier »). */
export const schemaDepotPieceCoffre = z.strictObject({
  type_piece: codePiece,
  titre: z.string().trim().min(1).max(300),
  expire_le: dateCivile.optional(),
});

export const schemaRechercheUsager = z.object({ identifiant: schemaIdentifiantUsager });
export const schemaFiltreCodesSimules = z.object({ destination: z.string().trim().max(254).optional() });

// Réponses -----------------------------------------------------------------------------------

export const schemaProfilUsager = z.object({
  id: z.string().uuid(),
  identifiant: z.string(),
  nom: z.string(),
  prenoms: z.string().nullable(),
  telephone: z.string().nullable(),
  courriel: z.string().nullable(),
  canal_prefere: z.enum(CANAUX_CODE).nullable(),
  identite_declaree: z.record(z.string(), z.unknown()).nullable(),
  identite_verifiee: z.object({ le: z.string(), type_piece: z.string(), date_piece: z.string(), collectivite: z.string().nullable() }).nullable(),
});
export type ProfilUsager = z.infer<typeof schemaProfilUsager>;

export const schemaRepresentation = z.object({
  id: z.string().uuid(),
  collectivite: z.object({ id: z.string().uuid(), code: z.string(), nom: z.string() }),
  entite_nom: z.string(),
  entite_type: z.enum(TYPES_ENTITE),
  entite_registre: z.string().nullable(),
  statut: z.enum(['en_attente', 'validee', 'refusee']),
  valide: z.boolean(),
  fin: z.string().nullable(),
  validee_le: z.string().nullable(),
  motif_refus: z.string().nullable(),
  mandataire: z.object({ compte_id: z.string().uuid(), nom: z.string() }).optional(),
  documents: z.object({ mandat: z.string().uuid().nullable(), extrait: z.string().uuid().nullable() }),
});

export const schemaPieceCoffre = z.object({
  id: z.string().uuid(),
  type_piece: z.string(),
  titre: z.string(),
  type_mime: z.string(),
  taille: z.number(),
  empreinte_sha256: z.string(),
  expire_le: z.string().nullable(),
  expiree: z.boolean(),
  jugee_conforme_le: z.string().nullable(),
  proposee_au_depot: z.boolean(),
  cree_le: z.string(),
});
